import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setSessionToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Target, CheckCircle, Loader2, User, MapPin,
  MessageSquare, Zap, Heart, Users, TrendingUp, Star, Award, Rocket,
  ChevronRight, GraduationCap
} from "lucide-react";

const PH_PROVINCES = ["Abra","Agusan del Norte","Agusan del Sur","Aklan","Albay","Antique","Apayao","Aurora","Basilan","Bataan","Batanes","Batangas","Benguet","Biliran","Bohol","Bukidnon","Bulacan","Cagayan","Camarines Norte","Camarines Sur","Camiguin","Capiz","Catanduanes","Cavite","Cebu","Cotabato","Davao de Oro","Davao del Norte","Davao del Sur","Davao Occidental","Davao Oriental","Dinagat Islands","Eastern Samar","Guimaras","Ifugao","Ilocos Norte","Ilocos Sur","Iloilo","Isabela","Kalinga","La Union","Laguna","Lanao del Norte","Lanao del Sur","Leyte","Maguindanao del Norte","Maguindanao del Sur","Marinduque","Masbate","Metro Manila","Misamis Occidental","Misamis Oriental","Mountain Province","Negros Occidental","Negros Oriental","Northern Samar","Nueva Ecija","Nueva Vizcaya","Occidental Mindoro","Oriental Mindoro","Palawan","Pampanga","Pangasinan","Quezon","Quirino","Rizal","Romblon","Samar","Sarangani","Siquijor","Sorsogon","South Cotabato","Southern Leyte","Sultan Kudarat","Sulu","Surigao del Norte","Surigao del Sur","Tarlac","Tawi-Tawi","Zambales","Zamboanga del Norte","Zamboanga del Sur","Zamboanga Sibugay"];
const EDU_LEVELS = ["High School","Senior High School","Vocational/Technical","Associate Degree","Bachelor's Degree","Master's Degree","Doctorate/PhD"];

export default function CoordinatorApply() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [step, setStep] = useState(0); // 0=landing, 1=login, 2=form-step1, 3=form-step2, 4=success
  const [submitting, setSubmitting] = useState(false);

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");

  // Application form state
  const [form, setForm] = useState({
    name: "", email: "", phone: "", facebook: "",
    address: "", city: "", province: "Metro Manila",
    edu_level: "", edu_school: "", edu_year: "",
    experience: "",
    q_how_know: "", q_why_interested: "", q_contribution: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email: loginEmail, password: loginPass });
      if (res.data.session_token) setSessionToken(res.data.session_token);
      setUser(res.data);
      await checkAuth();
      if (res.data.is_coordinator) {
        navigate("/recruitment");
      } else {
        toast.error("This login is for accepted Field Coordinators only.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid credentials"); }
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/coordinator/apply", form);
      setStep(4);
    } catch (err) { toast.error(err.response?.data?.detail || "Application failed"); }
    setSubmitting(false);
  };

  // ═══ STEP 0: WELCOME LANDING PAGE ═══
  if (step === 0) return (
    <div className="min-h-screen bg-white" data-testid="coord-landing">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F3D2E] via-[#1a5c44] to-[#0d4a35] text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/logo.png" alt="SetPlay" className="h-14 w-14 mx-auto mb-4" />
          <p className="text-[#FBBF24] font-heading uppercase tracking-[0.3em] text-sm mb-3">SetPlay Field Coordinator</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">Be Part of the Movement.<br/>Build the Game.</h1>
          <p className="text-xl text-white/80 mb-8">Change the Game.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button onClick={() => setStep(2)} size="lg" className="bg-[#FBBF24] text-[#0F3D2E] hover:bg-[#F59E0B] rounded-full px-10 h-14 font-heading uppercase" data-testid="coord-apply-btn">
              <Rocket className="w-5 h-5 mr-2" />Apply Now
            </Button>
            <Button onClick={() => setStep(1)} variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 h-14 font-heading uppercase" data-testid="coord-login-btn">
              Coordinator Login
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-16">
        {/* Section 1 */}
        <section>
          <h2 className="font-heading text-3xl font-bold text-[#0F3D2E] mb-4">Ano ang SetPlay Field Coordinator?</h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>Ang SetPlay Field Coordinator ang <strong>frontliner ng movement</strong>.</p>
            <p>Ikaw ang nasa ground — nakikipag-usap sa venue owners, coaches, LGUs, at community leaders.</p>
            <p>Ikaw ang tumutulong mag-onboard ng courts, gyms, studios, at sports facilities sa platform.</p>
            <p className="text-lg font-medium">In simple terms? <span className="text-[#0F3D2E]">Ikaw ang bridge between the digital platform and the real-world sports community.</span></p>
            <p className="text-[#DC2626] font-bold">Hindi lang ito trabaho. Mission ito!</p>
          </div>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="font-heading text-3xl font-bold text-[#0F3D2E] mb-4">Ano ang Gagawin Mo?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["Makipag-meeting sa venue owners at ipaliwanag kung paano sila makikinabang sa SetPlay",
              "Tumulong sa registration at onboarding ng venues",
              "Siguraduhin na kumpleto ang details (courts, schedules, pricing, features)",
              "Mag-coordinate sa coaches at sports professionals",
              "Mag-activate ng local sports communities",
              "Magbigay ng feedback from the ground para ma-improve ang platform",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                <ChevronRight className="w-5 h-5 text-[#0F3D2E] shrink-0 mt-0.5" />
                <p className="text-sm">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-lg font-medium text-[#0F3D2E] mt-4">You are not just executing tasks. You are helping build a nationwide sports ecosystem.</p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="font-heading text-3xl font-bold text-[#0F3D2E] mb-6">Bakit Rewarding ang Role na Ito?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {icon: Heart, title: "Impact-Driven Work", desc: "You're helping create access to sports. Mas maraming kabataan ang magkakaroon ng lugar para maglaro."},
              {icon: Rocket, title: "Build Something Bigger", desc: 'You\'re part of a startup building a national sports infrastructure. Masasabi mo: "Kasama ako nung nagsisimula pa lang."'},
              {icon: Users, title: "Network Expansion", desc: "Makikilala mo ang venue owners, entrepreneurs, coaches, athletes, LGU leaders. Real-world relationship building."},
              {icon: TrendingUp, title: "Entrepreneurial Exposure", desc: "Learn sales, negotiation, community building, operations, and startup execution. Parang mini-MBA."},
              {icon: Star, title: "Performance-Based Growth", desc: "May incentives, recognition, at potential leadership roles habang lumalaki ang platform."},
              {icon: Award, title: "Founding Team Member", desc: "Real-world startup experience. Personal growth in leadership and execution. Part of the transformation story."},
            ].map((item, i) => (
              <Card key={i} className="hover:-translate-y-1 transition-transform">
                <CardContent className="p-5">
                  <item.icon className="w-8 h-8 text-[#0F3D2E] mb-3" />
                  <h3 className="font-heading text-base font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Section 4 */}
        <section className="bg-[#0F3D2E] text-white rounded-2xl p-8 sm:p-12">
          <h2 className="font-heading text-3xl font-bold mb-4">Para Kanino Ito?</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {["Energetic and proactive na tao","Mahilig sa sports at community engagement","May strong communication skills",
              "Hindi nahihiya makipag-usap sa business owners","Gusto ng flexible but high-impact role","May hustler mindset pero may puso para sa community",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-white/10 rounded-lg"><CheckCircle className="w-4 h-4 text-[#FBBF24] shrink-0" /><p className="text-sm">{item}</p></div>
            ))}
          </div>
          <p className="text-lg mt-6 text-[#FBBF24] font-medium">Kung mahilig ka sa sports at gusto mong maging part ng something meaningful — this is your lane.</p>
        </section>

        {/* Section 5 */}
        <section className="text-center">
          <h2 className="font-heading text-3xl font-bold text-[#0F3D2E] mb-4">The Bigger Vision</h2>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">SetPlay is not just an app. It's a movement for physical wellness, community sports development, youth engagement, economic activation ng local venues, and nation-building through sports.</p>
          <div className="space-y-2 text-slate-700 max-w-xl mx-auto text-left">
            <p>Imagine helping digitize <strong>thousands of courts</strong> across the Philippines.</p>
            <p>Imagine creating <strong>easier access to coaches</strong> and safe play spaces.</p>
            <p>Imagine contributing to a <strong>healthier generation</strong>.</p>
            <p className="text-lg font-bold text-[#0F3D2E] text-center mt-4">That's what you're signing up for.</p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8">
          <Button onClick={() => setStep(2)} size="lg" className="bg-[#0F3D2E] hover:bg-[#1a5c44] text-white rounded-full px-12 h-14 text-lg font-heading uppercase">
            <Rocket className="w-5 h-5 mr-2" />Apply Now
          </Button>
        </section>
      </div>

      {/* Footer */}
      <div className="bg-[#0a2a1e] text-white/50 py-6 text-center text-sm">
        <p>Proudly Built for the Philippines by <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FBBF24] underline">SusTek Labs</a></p>
      </div>
    </div>
  );

  // ═══ STEP 1: COORDINATOR LOGIN ═══
  if (step === 1) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="coord-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="SetPlay" className="h-12 w-12 mx-auto mb-2" />
          <h1 className="font-heading text-xl font-bold">Field Coordinator Login</h1>
          <p className="text-xs text-muted-foreground">For accepted coordinators only</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div><Label>Email</Label><Input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required data-testid="coord-login-email" /></div>
              <div><Label>Password</Label><Input type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} required /></div>
              <Button type="submit" disabled={submitting} className="w-full rounded-full h-11 font-heading uppercase">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
        <div className="text-center mt-4">
          <button onClick={() => setStep(0)} className="text-sm text-primary hover:underline">Back to Welcome Page</button>
          <span className="mx-2 text-muted-foreground">·</span>
          <button onClick={() => setStep(2)} className="text-sm text-primary hover:underline">Apply as Coordinator</button>
        </div>
      </div>
    </div>
  );

  // ═══ STEP 2: APPLICATION FORM — Personal & Professional ═══
  if (step === 2) return (
    <div className="min-h-screen bg-slate-50">
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b">
        <div className="h-1 bg-primary transition-all" style={{ width: "50%" }} />
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => setStep(0)} className="flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="w-4 h-4" />Back</button>
          <span className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Step 1 of 2</span>
          <div />
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 pt-20 pb-12 space-y-5" data-testid="coord-step-1">
        <div className="flex items-center gap-3"><img src="/logo.png" alt="SetPlay" className="h-10 w-10" /><div><h2 className="font-heading text-lg font-bold">SetPlay</h2><p className="text-xs text-muted-foreground">Field Coordinator Application</p></div></div>
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><User className="w-5 h-5" />Personal & Professional Profile</h2>
        <div className="space-y-3">
          <div><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Juan Dela Cruz" data-testid="coord-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@email.com" data-testid="coord-email" /></div>
            <div><Label>Mobile Phone *</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+63 9XX XXX XXXX" /></div>
          </div>
          <div><Label>Facebook Handle / Profile</Label><Input value={form.facebook} onChange={e => setForm({...form, facebook: e.target.value})} placeholder="facebook.com/yourname" /></div>
          <Separator />
          <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Street, Barangay" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Municipality / City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
            <div><Label>Province</Label>
              <Select value={form.province} onValueChange={v => setForm({...form, province: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">{PH_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Separator />
          <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-1"><GraduationCap className="w-4 h-4" />Highest Educational Attainment</p>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Level</Label>
              <Select value={form.edu_level} onValueChange={v => setForm({...form, edu_level: v})}>
                <SelectTrigger data-testid="edu-level"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{EDU_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>School / University</Label><Input value={form.edu_school} onChange={e => setForm({...form, edu_school: e.target.value})} placeholder="e.g. UP Diliman" /></div>
            <div><Label>Year Completed</Label><Input value={form.edu_year} onChange={e => setForm({...form, edu_year: e.target.value})} placeholder="e.g. 2020" /></div>
          </div>
          <Separator />
          <div><Label>Professional Experience / Background</Label><Textarea value={form.experience} onChange={e => setForm({...form, experience: e.target.value})} placeholder="Tell us about your professional background, community involvement, sports experience..." className="text-sm" /></div>
        </div>
        <div className="flex justify-end"><Button onClick={() => setStep(3)} disabled={!form.name || !form.email} className="rounded-full px-8">Next <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
      </div>
    </div>
  );

  // ═══ STEP 3: THREE QUESTIONS ═══
  if (step === 3) return (
    <div className="min-h-screen bg-slate-50">
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b">
        <div className="h-1 bg-primary transition-all" style={{ width: "100%" }} />
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="w-4 h-4" />Back</button>
          <span className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Step 2 of 2</span>
          <div />
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 pt-20 pb-12 space-y-5" data-testid="coord-step-2">
        <div className="flex items-center gap-3"><img src="/logo.png" alt="SetPlay" className="h-10 w-10" /><div><h2 className="font-heading text-lg font-bold">SetPlay</h2><p className="text-xs text-muted-foreground">Field Coordinator Application</p></div></div>
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5" />Tell Us More About You</h2>
        <div className="space-y-4">
          <div><Label className="text-sm font-medium">How did you hear about SetPlay? *</Label><Textarea value={form.q_how_know} onChange={e => setForm({...form, q_how_know: e.target.value})} placeholder="Social media, friend referral, community event, news article..." className="mt-1" data-testid="q-how-know" /></div>
          <div><Label className="text-sm font-medium">Why are you interested in joining as a Field Coordinator? *</Label><Textarea value={form.q_why_interested} onChange={e => setForm({...form, q_why_interested: e.target.value})} placeholder="What motivates you? What do you hope to achieve?" className="mt-1" data-testid="q-why-interested" /></div>
          <div><Label className="text-sm font-medium">What can you contribute to grow SetPlay in your area? *</Label><Textarea value={form.q_contribution} onChange={e => setForm({...form, q_contribution: e.target.value})} placeholder="Your network, skills, local knowledge, community connections..." className="mt-1" data-testid="q-contribution" /></div>
        </div>
        <Button onClick={handleSubmit} disabled={submitting || !form.q_how_know || !form.q_why_interested || !form.q_contribution}
          className="w-full h-12 rounded-full font-heading uppercase" data-testid="coord-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}
        </Button>
      </div>
    </div>
  );

  // ═══ STEP 4: SUCCESS ═══
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="coord-success">
      <div className="text-center space-y-6 max-w-md">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto"><CheckCircle className="w-10 h-10 text-green-600" /></div>
        <h2 className="font-heading text-3xl font-bold text-green-700">Application Submitted!</h2>
        <p className="text-muted-foreground">Thank you for applying to be a SetPlay Field Coordinator. Our team will review your application and get back to you within 3-5 business days.</p>
        <p className="text-sm text-muted-foreground">Once accepted, you will receive an email with your login credentials.</p>
        <Button onClick={() => navigate("/")} className="rounded-full px-8">Back to Home</Button>
      </div>
    </div>
  );
}
